import { useEffect, useRef, useState } from 'react'
import { passkeysSupported, platformAuthenticatorAvailable, register, signIn } from '../lib/auth'
import { t, mt, getLocale, setLocale } from '../lib/i18n'
import { applyLook, choiceToPatch, saveLook, type LookChoice } from '../lib/look'
import { LookChoices } from '../market/LookPicker'
import { get } from '../lib/api'
import { installMode } from '../lib/install'
import InstallPrompt from './InstallPrompt'

/**
 * Sign in, or create an account. Passkeys only.
 *
 * There is no password field and no "forgot password" link, because there is no
 * password. That is stated on the screen rather than left for someone to
 * discover by looking for the missing control.
 */
/** Same rule as the server (auth.ts). Spaces become dots and accents are dropped, so a name typed here never gets refused for its shape. */
const HANDLE_OK = /^[a-z0-9][a-z0-9._-]{1,30}$/
const cleanHandle = (v: string) => v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '.').replace(/[^a-z0-9._-]/g, '').replace(/^[._-]+/, '').slice(0, 31)

export default function SignIn({ onSignedIn, onLanguage, inviteCode }: { onSignedIn: () => Promise<void>; onLanguage?: () => void; inviteCode?: string }) {
  const [mode, setMode] = useState<'in' | 'up'>(inviteCode ? 'up' : 'in')
  const [handle, setHandle] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  // The look chosen while signing up: previewed live, saved to the new account once it exists, dropped if nobody signs up.
  const [look, setLook] = useState<LookChoice>('shore')
  const made = useRef(false)
  // Whether creating an account needs the installed app, and whether sign-up is by invitation (asked once).
  const [gate, setGate] = useState<{ required: boolean; invitesOnly: boolean } | null>(null)
  const [typedCode, setTypedCode] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Whether WebAuthn exists at all decides what is shown; whether there is a
  // BUILT-IN authenticator only decides how the hint is worded.
  const supported = passkeysSupported()
  const [hasPlatform, setHasPlatform] = useState<boolean | null>(null)

  useEffect(() => {
    void platformAuthenticatorAvailable().then(setHasPlatform)
    void get<{ required: boolean; invitesOnly: boolean }>('/api/market/install-gate').then(setGate).catch(() => setGate({ required: false, invitesOnly: false }))
  }, [])
  useEffect(() => {
    if (mode === 'up') applyLook({ ...choiceToPatch(look), backdrop: choiceToPatch(look).backdrop ?? 'shore', sound: false })
    else applyLook(null)
    return () => { if (!made.current) applyLook(null) }
  }, [mode, look])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      if (mode === 'up') {
        made.current = true
        await saveLook(choiceToPatch(look)).catch(() => { /* the look can be chosen again on the Account page */ })
      }
      await onSignedIn()
    } catch (err) {
      /*
       * A cancelled ceremony is not an error worth alarming anyone about — the
       * user dismissed a system sheet. Everything else gets the real message.
       */
      const name = (err as { name?: string }).name
      if (name === 'NotAllowedError' || name === 'AbortError') setError(t('auth.cancelled'))
      else setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!supported) {
    return <section className="card"><p>{t('auth.noPasskeySupport')}</p></section>
  }

  // In a browser tab, creating an account is replaced by how to install first. Signing in is never blocked.
  if (mode === 'up' && gate?.required && installMode() === null) {
    return (
      <section className="card">
        <h1>{t('auth.createAccount')}</h1>
        <InstallPrompt invite={inviteCode}/>
        <p className="muted small">{mt('authHave')} <button className="link" onClick={() => setMode('in')}>{t('app.signIn')}</button></p>
      </section>
    )
  }

  return (
    <section className="card">
      <h1>{mode === 'in' ? t('app.signIn') : t('auth.createAccount')}</h1>

      <label>
        {t('auth.handle')}
        <input
          value={handle}
          onChange={(e) => { setHandle(cleanHandle(e.target.value)); setError(null) }}
          autoComplete="username webauthn"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="username"
          maxLength={31}
        />
        {mode === 'up' && <small className={handle && !HANDLE_OK.test(handle) ? 'handle-hint bad' : 'handle-hint'}>{getLocale() === 'en' ? 'Your sign-in name, one word: letters, numbers, dot, dash or underscore (2 to 31). You type your real name below.' : 'Tu nombre para entrar, una sola palabra: letras, números, punto, guion o guion bajo (2 a 31). Tu nombre de verdad va más abajo.'}</small>}
      </label>

      {mode === 'up' && (
        <fieldset className="lang-pick">
          <legend>{mt('langPickTitle')}</legend>
          <div role="radiogroup" aria-label={mt('langPickTitle')}>
            {(['es-PR', 'en'] as const).map(l => (
              <button type="button" key={l} role="radio" aria-checked={getLocale() === l} className={getLocale() === l ? 'on' : ''} onClick={() => { setLocale(l); onLanguage?.() }}>{l === 'es-PR' ? 'Español' : 'English'}</button>
            ))}
          </div>
          <small>{mt('langPickNote')}</small>
        </fieldset>
      )}

      {mode === 'up' && (
        <fieldset className="lang-pick look-pick">
          <legend>{mt('lookPickTitle')}</legend>
          <LookChoices value={look} label={mt('lookPickTitle')} onPick={setLook}/>
          <small>{mt('lookPickNote')}</small>
        </fieldset>
      )}

      {mode === 'up' && (
        <>
          <label>
            {t('auth.displayName')}
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" />
          </label>
          {gate?.invitesOnly && !inviteCode && (
            <label>
              {mt('inviteCodeLabel')}
              <input value={typedCode} onChange={(e) => setTypedCode(e.target.value)} autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
              <small>{mt('inviteCodeHint')}</small>
            </label>
          )}
          <label className="agree-row">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>
              {mt('agreeLead')} <a href="#/terms" target="_blank" rel="noopener noreferrer">{mt('terms')}</a> {mt('agreeAnd')}{' '}
              <a href="#/privacy" target="_blank" rel="noopener noreferrer">{mt('privacy')}</a>.
            </span>
          </label>
        </>
      )}

      <button
        className="primary"
        disabled={busy || (mode === 'up' && (!HANDLE_OK.test(handle) || !agreed))}
        onClick={() =>
          run(() =>
            mode === 'in'
              ? signIn(handle || undefined)
              : register({ handle, displayName, inviteCode: inviteCode ?? (typedCode.trim() || undefined) }),
          )
        }
      >
        {busy ? '…' : mode === 'in' ? t('auth.usePasskey') : t('auth.createAccount')}
      </button>

      {error && <p className="error" role="alert">{error}</p>}

      <p className="muted small">
        {mode === 'in' ? (
          <>{mt('authNo')} <button className="link" onClick={() => setMode('up')}>{t('auth.createAccount')}</button></>
        ) : (
          <>{mt('authHave')} <button className="link" onClick={() => setMode('in')}>{t('app.signIn')}</button></>
        )}
      </p>

      <p className="muted small">
        {mt('authNoPassword')} {hasPlatform === false ? mt('authExternalHint') : mt('authGeneralHint')}
      </p>
    </section>
  )
}
